import { useEffect, useRef, useState } from "react";
import { Loader2, MapPin, MapPinned, CircleCheckBig, TriangleAlert } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { newUi } from "../lib/featureFlags";
import { Field, ActionButton, SecondaryAction } from "./kit";
import { PLACE_TYPES, PLACE_TYPE_META } from "../lib/placeType";
import { GROUND_NAME_MAX, isVenueAuthority } from "../lib/groundEdit";
import { readDevicePosition, NEAR_ME_BUSY_LABEL } from "../lib/nearMe";
import { reverseGeocodePlace, forwardGeocodePlace } from "../lib/geocode";
import {
  ADD_GROUND_EYEBROW,
  ADD_GROUND_EYEBROW_HERE,
  ADD_GROUND_PIN_LEDE,
  ADD_GROUND_PIN_LABEL,
  ADD_GROUND_PIN_ACTION_LABEL,
  ADD_GROUND_TITLE,
  ADD_GROUND_LEDE,
  ADD_GROUND_LEDE_FUTURE,
  ADD_GROUND_NAME_PLACEHOLDER,
  ADD_GROUND_PLACE_TYPE_HELPER,
  ADD_GROUND_LOCATION_LABEL,
  ADD_GROUND_LOCATION_HELPER,
  ADD_GROUND_LOCATION_SAVED_NOTE,
  ADD_GROUND_ACTION_LABEL,
  ADD_GROUND_DECLINE_LABEL,
  ADD_GROUND_TOWN_FIELD_LABEL,
  ADD_GROUND_TOWN_PLACEHOLDER,
  ADD_GROUND_SAVE_TOWN_LABEL,
  ADD_GROUND_GEOCODE_FAILED,
  ADD_GROUND_CLOSING_LINE,
  ADD_GROUND_DUPLICATE_EYEBROW,
  ADD_GROUND_DUPLICATE_LINE,
  ADD_GROUND_DUPLICATE_NEARBY_LINE,
  ADD_GROUND_USE_FAILED,
  ADD_GROUND_USE_EXISTING_LABEL,
  ADD_GROUND_ADD_SURFACE_LABEL,
  ADD_GROUND_ADD_ANYWAY_LABEL,
  ADD_GROUND_DUPLICATE_CHECK_FAILED,
  ADD_GROUND_DUPLICATE_CHECK_RETRY,
} from "../lib/logGroundStep";
import { toast } from "sonner";
import { carriedSurfacePin } from "../lib/facilityAdd";
import { tapHalfLead } from "./kit/tapTarget";

/**
 * Issue #757 §3, matched against `FirstGround.dc.html` at
 * `design-baseline-round-4` (review round 2, B7) — "Add a ground", reduced
 * to one line and a location tap.
 *
 * Replaces `GroundSearchDialog`'s two-tab (search/manual) form for THIS
 * entry point only — `GroundSearchDialog` itself is untouched and still
 * serves every other caller (the grounds directory, the map, training).
 * This panel renders IN PLACE of `LogMatchGroundStep`'s list — "the same
 * step, not a second window" — never as a `Dialog`.
 *
 * HELD (do not relax without Andreas): `grounds.latitude`/`longitude` are
 * NOT NULL and stay that way. Both paths below — the one-tap location and
 * the declined-location town — always resolve a real coordinate BEFORE
 * `POST /grounds` fires; there is no third path that saves without one.
 * The API contract does not change (same payload shape
 * `GroundSearchDialog.jsx` already posts).
 *
 * WHERE THE DRAWING AND THE ISSUE'S HELD INSTRUCTION DISAGREE, NAMED RATHER
 * THAN PICKED SILENTLY: `FirstGround.dc.html:91` draws the closing line as
 * "Say no to location and the town is enough to save it. The pin can come
 * later…" — the issue's own §3 quotes that exact sentence and refutes it
 * ("It is not, today: POST /grounds requires latitude and longitude…").
 * This file keeps the issue's corrected line (`ADD_GROUND_CLOSING_LINE`,
 * minus the false "the town is enough" clause) rather than the drawn one —
 * the HELD instruction is the later, more specific authority on this one
 * sentence.
 */
/**
   * #1220 (D284, D285) — the Grounds screen's entries into this one panel.
   * `startWith="location"` reads the device position once on mount (the
   * "Where I'm standing" choice); the default "name" is the panel as it was.
   * `presetPin` ({ lat, lng, fromDevice? }) is a dropped pin: the save uses
   * those coordinates, and the panel shows them.
   * `onAddSurfaceInstead(ground, pin)` is the Grounds host's route into an
   * existing ground's card; the log and Plan hosts pass nothing and keep the
   * two-option prompt.
   * `future` (D394, #1473) — the game has not happened (Plan a match, Schedule a
   * game): the lede drops the standing-on-it idea. Default false: unchanged.
 */
export default function LogMatchAddGroundPanel({
  seed = "",
  onSaved,
  onCancel,
  onUseExisting,
  startWith = "name",
  presetPin = null,
  onAddSurfaceInstead = null,
  pageFeedback = false,
  future = false,
}) {
  const { user } = useAuth();
  const publishesOnCreate = isVenueAuthority(user);

  const [name, setName] = useState(seed);
  const [placeType, setPlaceType] = useState(null);
  const [town, setTown] = useState("");
  const [locating, setLocating] = useState(false);
  const [savingTown, setSavingTown] = useState(false);
  const [error, setError] = useState(null); // one plain line — location OR geocode failure
  // `startWith="location"`: the position read on mount, held until the save.
  const [heldPosition, setHeldPosition] = useState(null);
  const positionRead = useRef(null);

  // Issue #1080 (D212.4) — the duplicate check, asked before any POST. The
  // pattern is `GroundSearchDialog.checkForDuplicates` (#136, #633): the
  // rows, a "failed" flag (a 500 is not an all-clear), and the last question
  // asked so the retry re-asks it verbatim.
  const [duplicates, setDuplicates] = useState([]);
  const [checkingDup, setCheckingDup] = useState(false);
  const [dupCheckFailed, setDupCheckFailed] = useState(false);
  const [forcing, setForcing] = useState(false);
  const [useError, setUseError] = useState(false);
  // #1143 part 2: with `pageFeedback`, a failed save is said in the step, not
  // in a toast. Cleared by the next save attempt.
  const [saveFailed, setSaveFailed] = useState(false);
  const lastDupQuery = useRef(null);
  /** The payload the duplicate state is about, so "Add it anyway" re-posts
   * exactly that and nothing the contributor has since edited. */
  const pendingSave = useRef(null);
  const dupBlockRef = useRef(null);

  // One read, however many times the effect mounts (StrictMode runs it twice
  // in dev): the promise is kept, and each run settles its own state from it.
  useEffect(() => {
    if (startWith !== "location" || presetPin) return undefined;
    if (!positionRead.current) positionRead.current = readDevicePosition();
    let live = true;
    setLocating(true);
    positionRead.current
      .then((read) => {
        if (!live) return;
        if (read.ok) setHeldPosition(read.position);
        else setError(read.message);
      })
      .finally(() => { if (live) setLocating(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on mount
  }, []);

  const ready = name.trim().length > 0 && !!placeType;

  // Issue #996 (D201) — flag-on hides the Indoor chip; Field and Nets
  // remain. `PLACE_TYPES` itself (placeType.js) is untouched — the server
  // still accepts "indoor" and an existing indoor ground keeps displaying
  // everywhere else. Off-flag (neither flag on) this resolves to
  // PLACE_TYPES verbatim, so the flag-off chip grid stays byte-identical.
  //
  // PR #1007 review round 2, F4 — checks BOTH `newUi("log-ground")` and
  // `newUi("grounds")`, not "log-ground" alone. This panel is used by both
  // `LogGroundFlow.jsx` (mounted only while "log-ground" is already true)
  // and `GroundsNearYouScreen.jsx` (mounted only while "grounds" is
  // already true — a SEPARATE flag). Reading a single flag here worked by
  // coincidence on every leg this repo's own e2e suite walks (unset, or
  // `E2E_NEW_UI=all`, which turns every screen on together), but a
  // real-world allowlist that turns "grounds" on without "log-ground"
  // (e.g. a rollout that ships Grounds-near-you before the log flow) would
  // have kept offering Indoor there — the exact "apply the same rule
  // there" the issue asks for, for BOTH of this panel's callers
  // independently, not only when they happen to move together.
  const visiblePlaceTypes = (newUi("log-ground") || newUi("grounds")) ? PLACE_TYPES.filter((t) => t !== "indoor") : PLACE_TYPES;

  const clearDuplicateState = () => {
    lastDupQuery.current = null;
    pendingSave.current = null;
    setUseError(false);
    setDuplicates([]);
    setDupCheckFailed(false);
  };

  // A different name, place type or town is a different question: the rows
  // and the failure belong to the old one.
  const onNameChange = (e) => { setName(e.target.value); clearDuplicateState(); };
  const onTownChange = (e) => { setTown(e.target.value); clearDuplicateState(); };
  const onPlaceTypeChange = (t) => { setPlaceType(t); clearDuplicateState(); };

  /** Resolves "clear" (nothing found, go ahead), "found" (rows are on
   * screen) or "failed" (the failed block is on screen). */
  const checkForDuplicates = async (query) => {
    const { type, name: askedName, latitude, longitude } = query;
    lastDupQuery.current = query;
    const params = new URLSearchParams();
    params.set("place_type", type);
    if (askedName?.trim()) params.set("name", askedName.trim());
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      params.set("latitude", String(latitude));
      params.set("longitude", String(longitude));
    }
    setCheckingDup(true);
    try {
      const { data } = await api.get(`/grounds/check-duplicate?${params.toString()}`);
      const rows = Array.isArray(data) ? data : [];
      setDuplicates(rows);
      setDupCheckFailed(false);
      return rows.length > 0 ? "found" : "clear";
    } catch {
      // #633: cleared AND recorded — clearing alone made a 500 look like an
      // all-clear.
      setDuplicates([]);
      setDupCheckFailed(true);
      return "failed";
    } finally {
      setCheckingDup(false);
    }
  };

  const retryDuplicateCheck = () => {
    if (lastDupQuery.current) checkForDuplicates(lastDupQuery.current);
  };

  // The state appears below the fold of a long panel when it comes from the
  // town button; bring it into view.
  const showingDuplicateState = duplicates.length > 0 || dupCheckFailed;
  useEffect(() => {
    if (showingDuplicateState) dupBlockRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [showingDuplicateState]);

  /**
   * `sendPosition: false` is #134's rule, kept: when the coordinate is a
   * reading of where the player's body is (the one-tap path), the check is
   * name-only so the live position never reaches our request logs on a
   * second route. The save itself still carries it — that press is the
   * consent. A town geocode is not a body position, so it sends both.
   *
   * `force` skips the check for this one call: "Add it anyway" only. A
   * failed check waives nothing: the next press ASKS AGAIN, from whichever
   * path it comes. It posts only if the re-ask is clear, or fails again (the
   * failed state says "you can still save it" — this is that save).
   */
  const save = async (payload, { sendPosition = true, force = false } = {}) => {
    setSaveFailed(false);
    if (!force) {
      const failedBefore = dupCheckFailed;
      pendingSave.current = { payload, sendPosition };
      const verdict = await checkForDuplicates({
        type: payload.place_type,
        name: payload.name,
        latitude: sendPosition ? payload.latitude : undefined,
        longitude: sendPosition ? payload.longitude : undefined,
      });
      if (verdict === "found") return;
      if (verdict === "failed" && !failedBefore) return;
    }
    const { data } = await api.post(publishesOnCreate ? "/admin/grounds" : "/grounds", payload);
    // The Grounds host says it on the page ("<name> is saved to your grounds.").
    if (!pageFeedback) {
      toast.success(
        publishesOnCreate
          ? `"${data.name}" published to the shared directory`
          : `"${data.name}" added to your Grounds Map`,
      );
    }
    onSaved?.(data, { created: true });
  };

  /** One answer for the three save paths: on the page for the Grounds host,
   * a toast for every other host (D57 for the new look, as it was for the rest). */
  const reportSaveFailed = (e) => {
    if (pageFeedback) setSaveFailed(true);
    else toast.error(e?.response?.data?.detail || "Failed to add ground");
  };

  /** "Use this ground". A host that needs more than picking it (the Grounds
   * screen saves it to the player's grounds, D258) passes `onUseExisting`;
   * without it this only hands the row on, as the log and Plan hosts want. */
  const chooseExisting = async (g) => {
    if (forcing) return;
    setForcing(true);
    try {
      if (onUseExisting) await onUseExisting(g);
    } catch {
      setUseError(true);
      setForcing(false);
      return;
    }
    setForcing(false);
    onSaved?.(g, { created: false });
  };

  /** D284 — the existing ground is where the field or net goes. The host
   * routes to its card; this draft is abandoned. */
  const addSurfaceInstead = (g) => {
    // The pin that was about to become the ground is where the surface is
    // (#547): a dropped pin, or the town's geocoded point. A body position
    // (the one-tap path) never travels (#134).
    const asked = pendingSave.current;
    const pin = asked?.sendPosition
      ? { lat: asked.payload.latitude, lng: asked.payload.longitude, ...(presetPin?.fromDevice ? { fromDevice: true } : {}) }
      : null;
    onAddSurfaceInstead(g, carriedSurfacePin(pin, null));
    onCancel?.();
  };

  const addAnyway = async () => {
    const pending = pendingSave.current;
    if (!pending || forcing) return;
    setForcing(true);
    try {
      await save(pending.payload, { force: true });
    } catch (e) {
      reportSaveFailed(e);
    } finally {
      setForcing(false);
    }
  };

  /** The gold action — one tap reads the device position, reverse-geocodes
   * city/country exactly as `GroundSearchDialog` already does, and saves. */
  const addFromWhereIAm = async () => {
    if (!ready || locating) return;
    setError(null);
    setLocating(true);
    try {
      let position = presetPin || heldPosition;
      if (!position) {
        const read = await readDevicePosition();
        if (!read.ok) {
          setError(read.message);
          return;
        }
        position = read.position;
      }
      const { lat, lng } = position;
      const place = await reverseGeocodePlace(lat, lng);
      await save({
        name: name.trim(),
        city: place?.city || "Unknown",
        country: place?.country || "Unknown",
        latitude: lat,
        longitude: lng,
        place_type: placeType,
      }, { sendPosition: presetPin ? !presetPin.fromDevice : false });
    } catch (e) {
      reportSaveFailed(e);
    } finally {
      setLocating(false);
    }
  };

  /** The declined-location path — §3's HELD instruction: the town GEOCODES
   * to a coordinate, it never saves without one. */
  const saveWithTown = async () => {
    if (!ready || !town.trim() || savingTown) return;
    setError(null);
    setSavingTown(true);
    try {
      // With a dropped pin the town only names the place; the position stays
      // the pin's.
      const place = presetPin
        ? { lat: presetPin.lat, lng: presetPin.lng, country: (await reverseGeocodePlace(presetPin.lat, presetPin.lng))?.country }
        : await forwardGeocodePlace(town);
      if (!place) {
        setError(ADD_GROUND_GEOCODE_FAILED);
        return;
      }
      await save({
        name: name.trim(),
        city: town.trim(),
        country: place.country || "Unknown",
        latitude: place.lat,
        longitude: place.lng,
        place_type: placeType,
      }, { sendPosition: presetPin ? !presetPin.fromDevice : true });
    } catch (e) {
      reportSaveFailed(e);
    } finally {
      setSavingTown(false);
    }
  };

  return (
    <div className="nw-page-stack" data-testid="log-add-ground-panel" style={{ padding: "var(--nw-space-page)" }}>
      {/* Issue #757 review round 2, item 7 — a marker of its own, the same
          shape `log-step-1` gives the list view, so `spacing-tokens.spec.js`/
          `text-size-tokens.spec.js` can measure THIS container (not
          `log-add-ground-panel`'s own siblings — the measuring functions
          read the MARKER's parent, so the marker has to sit inside the
          container being measured, not stand in for it). */}
      <span data-testid="log-add-ground-panel-marker" className="sr-only" aria-hidden="true" />
      {/* Review round 3, F3 — FirstGround.dc.html:58 (and the round-5
          redraw brief) draws this as the panel's FIRST content element,
          not last under the closing line (where round 2 originally put
          it). Step 1's own "Back" (LogMatchGroundStep.jsx) was already
          first; this one moved to match. */}
      <SecondaryAction
        variant="link"
        onClick={onCancel}
        data-testid="log-add-ground-cancel"
        style={{
          // Block-level and shrink-wrapped, so it sits in the stack's flow
          // and its footprint stays one line (an inline box here added the
          // parent's strut and moved the title below it).
          display: "flex",
          width: "fit-content",
          justifyContent: "flex-start",
          alignSelf: "flex-start",
          // Second child of the page stack (an sr-only marker is first), so
          // the stack's own gap is added back (`tapTarget.js`).
          marginTop: `calc(var(--nw-space-stack) + ${tapHalfLead()})`,
          color: "var(--nw-ink-muted)",
          fontSize: 12,
          fontWeight: 400,
          textDecoration: "underline",
        }}
      >
        Back to the list
      </SecondaryAction>
      {/* #840 I3 (#1053 review round 2, F5): this comment used to say the
          rightLabel slot read "Step 1 of 3 · add a ground" — that suffix
          was removed in #757 review round 3. This panel mounts in TWO
          places, each inside its own navy TopBar with its own rightLabel,
          and this panel duplicates neither: `LogGroundFlow.jsx`'s TopBar
          reads plain "Step 1 of 3" (`<StepCounter step={1} total={3} />`),
          same as the list view it sits beside
          (`FirstGround52.dc.html:48`); `GroundsNearYouScreen.jsx`'s own
          TopBar (the "nothing near, add a ground" mount, `:237`) reads
          "Grounds" instead — a different screen's own label, not a
          leftover of this one. */}
      <div className="nw-stack-tight">
        <div className="nw-eyebrow">{!presetPin && startWith === "location" ? ADD_GROUND_EYEBROW_HERE : ADD_GROUND_EYEBROW}</div>
        <h1 className="nw-title">{ADD_GROUND_TITLE}</h1>
        <p className="nw-lede">{presetPin ? ADD_GROUND_PIN_LEDE : future ? ADD_GROUND_LEDE_FUTURE : ADD_GROUND_LEDE}</p>
      </div>

      <Field
        label="Ground name"
        data-testid="log-add-ground-name"
        placeholder={ADD_GROUND_NAME_PLACEHOLDER}
        maxLength={GROUND_NAME_MAX}
        value={name}
        onChange={onNameChange}
      />

      <div className="nw-stack-grid">
        <div className="nw-label">Place type</div>
        {/* Review round (#913, D155) — the helper sits directly under the
            "Place type" label, ABOVE the chip grid, not below it: label,
            helper, chips. */}
        <p className="nw-help">{ADD_GROUND_PLACE_TYPE_HELPER}</p>
        <div className="flex" style={{ gap: "var(--nw-space-grid)" }}>
          {visiblePlaceTypes.map((t) => {
            const meta = PLACE_TYPE_META[t];
            const active = placeType === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => onPlaceTypeChange(t)}
                aria-pressed={active}
                data-testid={`log-add-ground-place-type-${t}`}
                className="flex-1"
                style={{
                  minHeight: "var(--nw-size-chip)",
                  borderRadius: "var(--nw-radius-pill)",
                  border: active ? "2px solid var(--nw-navy)" : "1px solid var(--nw-line)",
                  background: active ? "var(--nw-navy-tint)" : "var(--nw-surface)",
                  color: active ? "var(--nw-navy)" : "var(--nw-ink-muted)",
                  fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                  fontSize: 14,
                  lineHeight: 1.4,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {meta.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* `FirstGround.dc.html:68-74` — a bordered card, icon + heading +
          helper together, not a bare label. */}
      <div
        className="nw-stack-grid"
        style={{
          padding: 14,
          background: "var(--nw-surface)",
          border: "1px solid var(--nw-line)",
          borderRadius: "var(--nw-radius-card)",
        }}
      >
        <div className="flex items-center" style={{ gap: "var(--nw-space-inline)" }}>
          <MapPinned size={20} style={{ color: "var(--nw-navy)" }} aria-hidden="true" />
          <span className="nw-body-strong">{presetPin ? ADD_GROUND_PIN_LABEL : ADD_GROUND_LOCATION_LABEL}</span>
        </div>
        <p className="nw-prose" style={{ color: "var(--nw-ink-lede)" }}>
          {presetPin ? `${presetPin.lat.toFixed(5)}, ${presetPin.lng.toFixed(5)}` : ADD_GROUND_LOCATION_HELPER}
        </p>
      </div>

      {/* `FirstGround.dc.html:76-79` — a separate confirmation box below
          the card, not folded into its helper text. */}
      <div
        className="flex items-center"
        style={{
          gap: "var(--nw-space-grid)",
          padding: "10px 12px",
          background: "var(--nw-surface)",
          border: "1px solid var(--nw-line)",
          borderRadius: "var(--nw-radius-control)",
        }}
      >
        <CircleCheckBig size={16} strokeWidth={2.5} style={{ color: "var(--nw-ok)" }} aria-hidden="true" />
        <span className="nw-help" style={{ color: "var(--nw-ink-lede)" }} data-testid="log-add-ground-location-saved-note">
          {ADD_GROUND_LOCATION_SAVED_NOTE}
        </span>
      </div>

      {/* Round 5 conformance, D55 — `--nw-alert` (#B42318) marks the glyph
          and left rule only; the text itself stays ink (`--nw-ink`), same
          as a field error. D97 drew this state (`town-not-found`,
          `FirstGround.dc.html`'s commentary block, excluded from
          structure-check by `data-dc-note`) in exactly this shape —
          glyph + rule, ink text — so this is no longer a decision-only
          inference; the build matches the drawing directly. */}
      {error && (
        <div
          role="alert"
          data-testid="log-add-ground-error"
          className="flex items-start"
          style={{ gap: "var(--nw-space-inline)", borderLeft: "3px solid var(--nw-alert)", paddingLeft: 10 }}
        >
          <TriangleAlert size={14} style={{ color: "var(--nw-alert)", flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
          <p className="nw-help" style={{ color: "var(--nw-ink)" }}>
            {error}
          </p>
        </div>
      )}

      {/* Issue #1080 (D212.4) — undrawn by ruling: FirstGround.dc.html draws
          no duplicate state. Kit pieces only and no gold (the panel's one
          gold stays its action); said on the page, never as a toast (D57). */}
      {(duplicates.length > 0 || dupCheckFailed) && (
        <div ref={dupBlockRef} className="nw-stack-grid">
          {dupCheckFailed && (
            <div
              role="alert"
              data-testid="log-add-ground-duplicate-check-failed"
              className="flex items-start"
              style={{ gap: "var(--nw-space-inline)", borderLeft: "3px solid var(--nw-alert)", paddingLeft: 10 }}
            >
              <TriangleAlert size={14} style={{ color: "var(--nw-alert)", flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
              <div className="nw-stack-xtight">
                <p className="nw-help" style={{ color: "var(--nw-ink)" }}>{ADD_GROUND_DUPLICATE_CHECK_FAILED}</p>
                <SecondaryAction
                  variant="link"
                  onClick={retryDuplicateCheck}
                  disabled={checkingDup}
                  data-testid="log-add-ground-duplicate-check-retry"
                  style={{ display: "flex", width: "fit-content", justifyContent: "flex-start", textDecoration: "underline" }}
                >
                  {ADD_GROUND_DUPLICATE_CHECK_RETRY}
                </SecondaryAction>
              </div>
            </div>
          )}
          {duplicates.length > 0 && (
            <div data-testid="log-add-ground-duplicate-warning" className="nw-stack-grid">
              <div className="nw-eyebrow">{ADD_GROUND_DUPLICATE_EYEBROW}</div>
              <p className="nw-prose" style={{ color: "var(--nw-ink-lede)" }} data-testid="log-add-ground-duplicate-line">
                {/* The SERVER says why each row was offered (`matched_by`, D257); a row
                    without the field is treated as a name match, the wording
                    that was shipped before it. */}
                {duplicates.some((g) => g.matched_by !== "position") ? ADD_GROUND_DUPLICATE_LINE : ADD_GROUND_DUPLICATE_NEARBY_LINE}
              </p>
              {useError && (
                <p role="alert" className="nw-help" data-testid="log-add-ground-use-failed" style={{ color: "var(--nw-ink)" }}>
                  {ADD_GROUND_USE_FAILED}
                </p>
              )}
              {duplicates.map((g) => (
                <div
                  key={g.ground_id}
                  data-testid={`log-add-ground-duplicate-${g.ground_id}`}
                  className="nw-stack-xtight"
                  style={{
                    padding: 14,
                    background: "var(--nw-surface)",
                    border: "1px solid var(--nw-line)",
                    borderRadius: "var(--nw-radius-card)",
                  }}
                >
                  <div className="nw-body-strong">{g.name}</div>
                  <div className="nw-help">{[g.city, g.country].filter(Boolean).join(", ") || "—"}</div>
                  <SecondaryAction
                    onClick={() => chooseExisting(g)}
                    disabled={forcing}
                    data-testid={`log-add-ground-use-${g.ground_id}`}
                  >
                    {ADD_GROUND_USE_EXISTING_LABEL}
                  </SecondaryAction>
                  {onAddSurfaceInstead && (
                    <SecondaryAction
                      onClick={() => addSurfaceInstead(g)}
                      disabled={forcing}
                      data-testid={`log-add-ground-add-surface-${g.ground_id}`}
                    >
                      {ADD_GROUND_ADD_SURFACE_LABEL}
                    </SecondaryAction>
                  )}
                </div>
              ))}
              <SecondaryAction
                variant="link"
                onClick={addAnyway}
                disabled={forcing}
                data-testid="log-add-ground-add-anyway"
                style={{ display: "flex", width: "fit-content", justifyContent: "flex-start", textDecoration: "underline" }}
              >
                {ADD_GROUND_ADD_ANYWAY_LABEL}
              </SecondaryAction>
            </div>
          )}
        </div>
      )}

      {/* Round 5 conformance, D27 (issue #864) — the gold action's label
          and icon default to near-black ink in the kit component itself
          now; FirstGround.dc.html:86-88 draws this button's label and icon
          the same near-black ink. No per-call override needed here any
          more. */}
      {saveFailed && (
        <div
          role="alert"
          data-testid="log-add-ground-save-failed"
          className="flex items-start"
          style={{ gap: 10, padding: "12px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderLeft: "3px solid var(--nw-alert)", borderRadius: 10 }}
        >
          <TriangleAlert size={16} style={{ color: "var(--nw-alert)", flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
          <div className="flex flex-col" style={{ gap: 2 }}>
            <span style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4, color: "var(--nw-ink)" }}>Failed to add ground</span>
            <span style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>Nothing you typed is lost. Try again.</span>
          </div>
        </div>
      )}

      <ActionButton
        onClick={addFromWhereIAm}
        disabled={!ready || locating}
        data-testid="log-add-ground-save"
        icon={locating ? <Loader2 size={20} className="animate-spin" /> : <MapPin size={20} />}
      >
        {locating ? NEAR_ME_BUSY_LABEL : presetPin ? ADD_GROUND_PIN_ACTION_LABEL : ADD_GROUND_ACTION_LABEL}
      </ActionButton>

      <div className="nw-stack-grid">
        <div className="nw-section">{ADD_GROUND_DECLINE_LABEL}</div>
        <div className="nw-stack-xtight">
          {/* Review round 3 (#907, F1): a real `<label htmlFor>`, tied to
              the input's own id — same fix as `kit/Field.jsx`'s own label,
              kept as a separate sibling (not moved into Field's own
              `label` prop) so the DOM/CSS this wrapper's `nw-stack-xtight`
              gap already produces stays byte-identical. */}
          <label htmlFor="log-add-ground-town" className="nw-label" style={{ display: "block" }}>
            {ADD_GROUND_TOWN_FIELD_LABEL}
          </label>
          <Field
            id="log-add-ground-town"
            data-testid="log-add-ground-town"
            placeholder={ADD_GROUND_TOWN_PLACEHOLDER}
            value={town}
            onChange={onTownChange}
          />
        </div>
        {/* Review round 3, F2 — FirstGround.dc.html:103 draws this full
            width, white background, navy text, 1px navy border — not
            `variant="boxed"`'s defaults (`--nw-line` border, `--nw-ink-muted`
            text, content-sized width; measured 177px on a 350px column
            before this fix). `variant="navy-outline"` is a new SHARED kit
            option (`SecondaryAction.jsx`), not a restyle of `boxed`'s own
            default — every existing `boxed`/`link` caller is unaffected. */}
        <SecondaryAction
          variant="navy-outline"
          onClick={saveWithTown}
          disabled={!ready || !town.trim() || savingTown}
          data-testid="log-add-ground-save-town"
        >
          {savingTown ? "Saving…" : ADD_GROUND_SAVE_TOWN_LABEL}
        </SecondaryAction>
        {!presetPin && <p className="nw-help">{ADD_GROUND_CLOSING_LINE}</p>}
      </div>
    </div>
  );
}
